import { useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { STEPS, Icons, formatCurrentDate } from "../../../components/Booking/bookingConstants";
import HorizontalStepper from "../../../components/Booking/HorizontalStepper";
import LivePreview from "../../../components/Booking/LivePreview";
import StepBookingDetails from "../../../components/Booking/StepBookingDetails";
import StepProducts from "../../../components/Booking/StepProducts";
import StepTravelers from "../../../components/Booking/StepTravelers";
import StepAssignments from "../../../components/Booking/StepAssignments";
import StepSSR from "../../../components/Booking/StepSSR";
import StepPricing from "../../../components/Booking/StepPricing";
import StepSummary from "../../../components/Booking/StepSummary";
import { useCreatePrivateCustomer, useCreateProfessionalCustomer } from "../../../api/people/usePeopleApi";
import { useToast } from "../../../context/ToastContext";
import { useLinkImportToBooking } from "../../../api/booking/useImportApi";
import {
  useCreateBooking,
  useAddBookingProducts,
  useDeleteBookingProduct,
  useAddBookingTravelers,
  useDeleteBookingTraveler,
  useCreateBookingAssignments,
  useDeleteBookingAssignment,
  useAddBookingSSRs,
  useDeleteBookingSSR,
  useUpdateSSRStatus,
  useSetFlightPricing,
  useSetHotelPricing,
  useSetTransferPricing,
  useSetGenericPricing,
  useSetSSRPricing,
  useSubmitBooking,
  useCalculateVAT,
} from "../../../api/booking/useBookingApi";
import styles from "./AddBooking.module.css";

const AddBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill ?? null;
  const { showToast } = useToast();
  const { execute: createBooking, loading: creatingBooking } = useCreateBooking();
  const { execute: saveProducts, loading: savingProducts } = useAddBookingProducts();
  const { execute: deleteBookingProduct } = useDeleteBookingProduct();
  const { execute: saveTravelers, loading: savingTravelers } = useAddBookingTravelers();
  const { execute: deleteBookingTraveler } = useDeleteBookingTraveler();
  const { execute: createAssignments, loading: savingAssignments } = useCreateBookingAssignments();
  const { execute: deleteBookingAssignment } = useDeleteBookingAssignment();
  const { execute: saveSSRs, loading: savingSSRs } = useAddBookingSSRs();
  const { execute: deleteBookingSSR } = useDeleteBookingSSR();
  const { execute: updateSSRStatusInline } = useUpdateSSRStatus();
  const { execute: setFlightPricing, loading: savingFlightPricing } = useSetFlightPricing();
  const { execute: setHotelPricing, loading: savingHotelPricing } = useSetHotelPricing();
  const { execute: setTransferPricing, loading: savingTransferPricing } = useSetTransferPricing();
  const { execute: setGenericPricing, loading: savingGenericPricing } = useSetGenericPricing();
  const { execute: setSSRPricing, loading: savingSSRPricing } = useSetSSRPricing();
  const { execute: submitBookingApi, loading: submittingBooking } = useSubmitBooking();
  const { execute: linkImport } = useLinkImportToBooking();
  const { execute: calculateVAT } = useCalculateVAT();
  const { execute: createPrivateCustomer, loading: creatingPrivateCustomer } = useCreatePrivateCustomer();
  const { execute: createProfessionalCustomer, loading: creatingProfessionalCustomer } = useCreateProfessionalCustomer();

  const [bookingId, setBookingId] = useState(null);
  // Reference is assigned by the backend (Sequence Settings) once the booking is created in Step 1.
  const [bookingRef, setBookingRef] = useState("");
  const [step, setStep] = useState(1);
  const [currentDate] = useState(formatCurrentDate);
  const [data, setData] = useState(() => ({
    bookingType: prefill?.bookingType ?? "", saleType: "",
    customerType: prefill?.customerType ?? "Private",
    customer: null, isNewCustomer: false,
    newCustomer: {
      // Private fields
      firstName: "", lastName: "", email: "", phone: "", dateOfBirth: "", gender: "",
      nationality: "", language: "",
      street: "", houseNumber: "", boxNumber: "", city: "", zip: "", country: "",
      // Company fields
      companyName: "", legalName: "", vatNumber: "", industry: "", website: "",
      invoiceEmail: "", invoiceAddress: "", paymentTerms: "30",
      contactFirstName: "", contactLastName: "", contactEmail: "", contactPhone: "",
      contactRole: "", contactLanguage: "",
    },
    // An Airtuerk import arrives here fully mapped — flight, legs, travellers,
    // assignments and per-traveller pricing. Anything the feed could not state
    // (dates of birth, a purchase price) is deliberately left blank so the
    // wizard's own validation asks for it, rather than being filled with a
    // placeholder that would pass validation and be wrong.
    products: prefill?.products ?? [],
    travelers: prefill?.travelers ?? [],
    assignments: prefill?.assignments ?? [],
    ssrItems: [],
    pricing: prefill?.pricing ?? {},
    _prefillCustomer: prefill?.customer ?? null,
    bookingStatus: "Pending",
    paymentStatus: "Unpaid",
    adjustments: [],
    notes: [],
    isLocked: false,
    lockedAt: null,
    lockedBy: null,
    invoiceNumber: null,
    createdBy: "Current User",
    creationDate: new Date().toISOString(),
  }));

  const handleChange = useCallback((field, value) => setData((prev) => ({ ...prev, [field]: value })), []);

  const canProceed = useMemo(() => {
    if (step === 1) {
      if (!data.bookingType) return false;
      if (data._prefillCustomer || data.customer) return true;
      if (data.isNewCustomer) {
        const nc = data.newCustomer;
        if (data.customerType === "Company") {
          const contactEmailOk = nc.hasContactEmail === false || !!nc.contactEmail;
          return !!(nc.companyName && nc.legalName && nc.vatNumber && nc.industry && nc.country && nc.invoiceEmail && nc.contactFirstName && nc.contactLastName && contactEmailOk && nc.contactPhone);
        }
        const emailOk = nc.hasEmail === false || !!nc.email;
        return !!(nc.firstName && nc.lastName && emailOk && nc.phone && nc.nationality && nc.language);
      }
      return false;
    }
    if (step === 2) return data.products.length > 0;
    if (step === 3) return data.travelers.length > 0 && data.travelers.every((t) => t.firstName && t.lastName && t.dob);
    if (step === 4) return (data.assignments || []).length > 0;
    if (step === 5) return true;
    if (step === 6) return Object.keys(data.pricing).length > 0;
    return true;
  }, [step, data]);

  const advanceStep = (next) => { setStep(next); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const resolveCustomer = () => {
    const c = data._prefillCustomer || data.customer;
    if (!c?.id) return null;
    const type = c.type === "Company" ? "Company" : "Private";
    return {
      customerId: c.id,
      customerType: type,
      customerName: c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.tradingName || c.legalName || undefined,
      customerEmail: c.email || c.invoiceEmail || undefined,
      customerPhone: c.phone || c.primaryContactPhone || undefined,
    };
  };

  const handleRemoveProduct = useCallback(async (product) => {
    if (product?._serverId && bookingId) {
      const result = await deleteBookingProduct(bookingId, product._serverId);
      if (!result.success) {
        showToast(result.message || "Failed to remove product on server", "error");
        return;
      }
    }
    setData((prev) => {
      // Drop every pricing entry belonging to the removed product so its
      // financials don't linger in the Pricing step totals. Keys take the form
      // `${ti}-${PRD-id}`, `${ti}-${PRD-id}-Outbound/Inbound`, `hotelroom-${PRD-id}-...`,
      // `transfer-${PRD-id}` and `single-${PRD-id}` — all contain the PRD id token.
      // Match the exact token (not substring) to avoid PRD-1 also hitting PRD-12.
      const pricing = { ...prev.pricing };
      Object.keys(pricing).forEach((k) => {
        if (k.startsWith("ssr-")) return;
        const m = k.match(/PRD-\d+/);
        if (m && m[0] === product.id) delete pricing[k];
      });
      return {
        ...prev,
        products: prev.products.filter((p) => p.id !== product.id),
        assignments: prev.assignments.filter((a) => a.productId !== product.id),
        pricing,
      };
    });
  }, [bookingId, deleteBookingProduct, showToast]);

  const handleStep2Continue = async () => {
    if (!bookingId) {
      showToast("Create the booking in Step 1 before adding products", "error");
      return;
    }
    const result = await saveProducts(bookingId, data.products);
    if (!result.success) {
      showToast(result.message || "Failed to save products", "error");
      return;
    }
    setData((prev) => ({
      ...prev,
      products: prev.products.map((p) => {
        const idx = prev.products.findIndex((sp) => sp.id === p.id);
        if (idx === -1) return p;
        const action = result.data?.[idx];
        return action?.productId ? { ...p, _serverId: action.productId } : p;
      }),
    }));
    showToast(`${result.data?.length || 0} product(s) saved`, "success");
    advanceStep(3);
  };

  const handleRemoveTraveler = useCallback(async (traveler, index) => {
    if (traveler?._serverId && bookingId) {
      const result = await deleteBookingTraveler(bookingId, traveler._serverId);
      if (!result.success) {
        showToast(result.message || "Failed to remove traveler on server", "error");
        return;
      }
    }
    setData((prev) => {
      const travelers = prev.travelers.filter((_, i) => i !== index);
      const assignments = prev.assignments
        .filter((a) => a.travelerIndex !== index)
        .map((a) => ({ ...a, travelerIndex: a.travelerIndex > index ? a.travelerIndex - 1 : a.travelerIndex }));
      return { ...prev, travelers, assignments };
    });
  }, [bookingId, deleteBookingTraveler, showToast]);

  const handleStep3Continue = async () => {
    if (!bookingId) {
      showToast("Complete Step 1 before saving travelers", "error");
      return;
    }
    const result = await saveTravelers(bookingId, data.travelers);
    if (!result.success) {
      showToast(result.message || "Failed to save travelers", "error");
      return;
    }
    setData((prev) => ({
      ...prev,
      travelers: prev.travelers.map((t, i) => {
        const saved = result.data?.[i];
        return saved?.travelerId ? { ...t, _serverId: saved.travelerId, type: saved.type, age: saved.age } : t;
      }),
    }));
    showToast(`${result.data?.length || 0} traveler(s) saved`, "success");
    advanceStep(4);
  };

  const handleRemoveAssignment = useCallback(async (assignment) => {
    if (assignment?._serverId && bookingId) {
      const result = await deleteBookingAssignment(bookingId, assignment._serverId);
      if (!result.success) {
        showToast(result.message || "Failed to remove assignment on server", "error");
        return;
      }
    }
    setData((prev) => ({
      ...prev,
      assignments: prev.assignments.filter(
        (a) => !(a.travelerIndex === assignment.travelerIndex && a.productId === assignment.productId),
      ),
    }));
  }, [bookingId, deleteBookingAssignment, showToast]);

  const handleStep4Continue = async () => {
    if (!bookingId) {
      showToast("Complete Step 1 before saving assignments", "error");
      return;
    }
    if (data.assignments.length === 0) {
      showToast("Add at least one assignment", "error");
      return;
    }

    const pending = data.assignments.filter((a) => !a._serverId);
    if (pending.length === 0) {
      advanceStep(5);
      return;
    }

    const missing = pending.filter((a) => {
      const product = data.products.find((p) => p.id === a.productId);
      const traveler = data.travelers[a.travelerIndex];
      return !product?._serverId || !traveler?._serverId;
    });
    if (missing.length > 0) {
      showToast("Some products or travelers haven't been saved yet. Re-save Steps 2 and 3.", "error");
      return;
    }

    const apiAssignments = pending.map((a) => {
      const product = data.products.find((p) => p.id === a.productId);
      const traveler = data.travelers[a.travelerIndex];
      const payload = { productId: product._serverId, travelerId: traveler._serverId };
      if (a.seatNumber) payload.seatNumber = a.seatNumber;
      if (a.roomNumber) payload.roomNumber = a.roomNumber;
      if (a.segmentId) payload.segmentId = a.segmentId;
      return payload;
    });

    const result = await createAssignments(bookingId, apiAssignments);
    if (!result.success) {
      showToast(result.message || "Failed to save assignments", "error");
      return;
    }

    setData((prev) => {
      const saved = result.data || [];
      const assignments = prev.assignments.map((a) => {
        if (a._serverId) return a;
        const product = prev.products.find((p) => p.id === a.productId);
        const traveler = prev.travelers[a.travelerIndex];
        if (!product?._serverId || !traveler?._serverId) return a;
        const match = saved.find(
          // Use Number() on both sides — backend returns productId/travelerId as integers,
          // but Object.entries() in the backend can produce string keys; defensive cast.
          (s) => Number(s.productId) === Number(product._serverId) && Number(s.travelerId) === Number(traveler._serverId),
        );
        return match?.assignmentId
          ? { ...a, _serverId: match.assignmentId, ticketNumber: match.ticketNumber || a.ticketNumber }
          : a;
      });
      return { ...prev, assignments };
    });
    showToast(`${result.data?.length || 0} assignment(s) saved`, "success");
    advanceStep(5);
  };

  const handleRemoveSSR = useCallback(async (ssr) => {
    if (ssr?._serverId && bookingId) {
      const result = await deleteBookingSSR(bookingId, ssr._serverId);
      if (!result.success) {
        showToast(result.message || "Failed to remove SSR on server", "error");
        return;
      }
    }
    setData((prev) => ({
      ...prev,
      ssrItems: prev.ssrItems.filter((s) => s !== ssr),
    }));
  }, [bookingId, deleteBookingSSR, showToast]);

  // Persist a status change for an SSR that's already saved to the server
  // (has a _serverId). Rows not yet saved keep their status locally and are
  // sent on the first Step-5 save.
  const handlePersistSSRStatus = useCallback(async (serverId, status) => {
    if (!serverId || !bookingId) return;
    const result = await updateSSRStatusInline(bookingId, serverId, status);
    if (result.success) {
      showToast(`SSR status set to ${status}`, "success");
    } else {
      showToast(result.message || "Failed to update SSR status", "error");
    }
  }, [bookingId, updateSSRStatusInline, showToast]);

  const handleStep5Continue = async () => {
    if (!bookingId) {
      showToast("Complete Step 1 before saving SSRs", "error");
      return;
    }
    const ssrItems = data.ssrItems || [];
    if (ssrItems.length === 0) {
      advanceStep(6);
      return;
    }

    const pending = ssrItems
      .map((s, idx) => ({ s, idx }))
      .filter(({ s }) => !s._serverId);

    if (pending.length === 0) {
      advanceStep(6);
      return;
    }

    const apiSsrs = [];
    const pendingMeta = [];
    for (const { s, idx } of pending) {
      const traveler = s.travelerId !== undefined && s.travelerId !== "__all__"
        ? data.travelers[parseInt(s.travelerId, 10)]
        : null;
      const product = s.flightProductId
        ? data.products.find((p) => p.id === s.flightProductId)
        : null;
      if (s.travelerId !== undefined && s.travelerId !== "__all__" && !traveler?._serverId) {
        showToast("Some travelers haven't been saved yet. Re-save Step 3.", "error");
        return;
      }
      if (s.flightProductId && !product?._serverId) {
        showToast("Some flight products haven't been saved yet. Re-save Step 2.", "error");
        return;
      }
      apiSsrs.push({
        travelerId: traveler?._serverId,
        productId: product?._serverId,
        ssrCode: s.code,
        ssrName: s.label,
        ssrCategory: s.category,
        ssrDescription: s.desc || undefined,
        quantity: s.qty || 1,
        status: s.status || "Requested",
      });
      pendingMeta.push(idx);
    }

    const result = await saveSSRs(bookingId, apiSsrs);
    if (!result.success) {
      showToast(result.message || "Failed to save SSRs", "error");
      return;
    }

    setData((prev) => {
      const saved = result.data || [];
      const next = [...prev.ssrItems];
      pendingMeta.forEach((origIdx, i) => {
        const matched = saved[i];
        if (matched?.ssrId && next[origIdx]) {
          next[origIdx] = { ...next[origIdx], _serverId: matched.ssrId };
        }
      });
      return { ...prev, ssrItems: next };
    });
    showToast(`${result.data?.length || 0} SSR(s) saved`, "success");
    advanceStep(6);
  };

  const handleStep6Continue = async () => {
    if (!bookingId) {
      showToast("Complete Step 1 before saving pricing", "error");
      return;
    }
    const pricing = data.pricing || {};
    if (Object.keys(pricing).length === 0) {
      showToast("Add pricing before continuing", "error");
      return;
    }

    /* IMPORTANT: use thunks (lazy functions), NOT pre-started Promises.
       If you push setHotelPricing(...) directly, the HTTP request fires immediately
       and all three pricing calls run concurrently — each recalculateBookingTotals
       sees only its own product (REPEATABLE READ snapshot isolation) and the last
       commit "wins", leaving the booking with a partial grandTotal. */
    const pricingTasks = [];
    const missingPricing = [];

    /* Flight pricing — group entries by productId, then by legType */
    const flightProducts = (data.products || []).filter((p) => p.type === "Flight" && p._serverId);
    for (const product of flightProducts) {
      const isSplit = product.flightPricingMode === "SPLIT";
      const directions = isSplit ? ["Outbound", "Inbound"] : [null];
      const productLabel = `Flight ${product.flightNumber || product.id}`;
      for (const dir of directions) {
        const travelerPricing = [];
        const tis = [...new Set(
          (data.assignments || [])
            .filter((a) => a.productId === product.id)
            .map((a) => a.travelerIndex),
        )];
        for (const ti of tis) {
          const traveler = data.travelers[ti];
          if (!traveler?._serverId) continue;
          const key = dir ? `${ti}-${product.id}-${dir}` : `${ti}-${product.id}`;
          const p = pricing[key];
          if (!p) continue;
          travelerPricing.push({
            travelerId: traveler._serverId,
            legType: dir || null,
            ticketNumber: p.ticketNumber || undefined,
            netPrice: p.netPrice || 0,
            tax: p.tax || 0,
            vatTax: p.vatTax || 0,
            commission: p.commission || 0,
            segmentFee: p.segFee || 0,
          });
        }
        if (travelerPricing.length > 0) {
          pricingTasks.push(
            () => setFlightPricing(bookingId, { productId: product._serverId, travelerPricing }),
          );
        } else {
          missingPricing.push(dir ? `${productLabel} (${dir})` : productLabel);
        }
      }
    }

    /* Hotel pricing — collect rooms per product */
    const hotelProducts = (data.products || []).filter((p) => p.type === "Hotel" && p._serverId);
    for (const product of hotelProducts) {
      const roomPricing = [];
      const rooms = Array.isArray(product.rooms) && product.rooms.length > 0
        ? product.rooms
        : Array.from({ length: Math.max(1, parseInt(product.numberOfRooms, 10) || 1) }, (_, i) => ({ id: `legacy-${product.id}-${i}` }));
      for (const room of rooms) {
        const key = `hotelroom-${product.id}-${room.id}`;
        const p = pricing[key];
        if (!p) continue;
        roomPricing.push({
          roomId: room.id,
          netPrice: p.netPrice || 0,
          markup: p.markup || 0,
          vat: p.vatTax || 0,
        });
      }
      if (roomPricing.length > 0) {
        pricingTasks.push(
          () => setHotelPricing(bookingId, { productId: product._serverId, roomPricing }),
        );
      } else {
        missingPricing.push(`Hotel ${product.hotelName || product.id}`);
      }
    }

    /* Transfer pricing — backend takes single cost/markup/vat per product (fixed price, not per traveler) */
    const transferProducts = (data.products || []).filter((p) => p.type === "Transfer" && p._serverId);
    for (const product of transferProducts) {
      const key = `transfer-${product.id}`;
      const p = pricing[key];
      const costPrice = p?.costPrice || 0;
      const markup = p?.markup || 0;
      const vat = p?.vatTax || 0;
      if (costPrice > 0 || markup > 0 || vat > 0) {
        pricingTasks.push(
          () => setTransferPricing(bookingId, { productId: product._serverId, costPrice, markup, vat }),
        );
      } else {
        missingPricing.push(`Transfer ${product.transferType || product.id}`);
      }
    }

    /* Generic product pricing (Insurance, CarRental, Visa, Cruise, Package, Incoming) — per-traveler */
    const GENERIC_TYPES = ["Insurance", "Package", "CarRental", "Visa", "Cruise", "Incoming"];
    const genericProducts = (data.products || []).filter((p) => GENERIC_TYPES.includes(p.type) && p._serverId);
    for (const product of genericProducts) {
      const travelerPricing = [];
      const tis = [...new Set(
        (data.assignments || [])
          .filter((a) => a.productId === product.id)
          .map((a) => a.travelerIndex),
      )];
      for (const ti of tis) {
        const traveler = data.travelers[ti];
        if (!traveler?._serverId) continue;
        const key = `${ti}-${product.id}`;
        const p = pricing[key];
        if (!p) continue;
        travelerPricing.push({
          travelerId: traveler._serverId,
          costPrice: p.costPrice || 0,
          markup: p.markup || 0,
          vatTax: p.vatTax || 0,
        });
      }
      if (travelerPricing.length > 0) {
        pricingTasks.push(
          () => setGenericPricing(bookingId, { productId: product._serverId, travelerPricing }),
        );
      } else {
        missingPricing.push(`${product.type} ${product.supplier || product.id}`);
      }
    }

    /* SSR pricing — one PUT per saved SSR */
    for (const ssr of data.ssrItems || []) {
      if (!ssr._serverId) continue;
      const key = `ssr-${ssr.itemId}-${ssr.travelerId}`;
      const p = pricing[key];
      if (!p) continue;
      pricingTasks.push(
        () => setSSRPricing(bookingId, {
          ssrId: ssr._serverId,
          costPrice: p.costPrice || 0,
          vatTax: p.vatTax || 0,
          markup: p.markup || 0,
        }),
      );
    }

    if (missingPricing.length > 0) {
      showToast(`Add pricing for: ${missingPricing.join(", ")}`, "error");
      return;
    }

    if (pricingTasks.length === 0) {
      showToast("No pricing entries match saved products/SSRs", "error");
      return;
    }

    /* Run pricing saves TRULY SEQUENTIALLY using thunks.
       Each task() call starts ONE HTTP request and awaits it before moving on.
       This ensures each server-side recalculateBookingTotals sees all previously
       committed products, avoiding the REPEATABLE READ race condition where every
       concurrent transaction only sees its own product and overwrites grandTotal. */
    const results = [];
    for (const task of pricingTasks) {
      const result = await task();     // ← starts the request here, one at a time
      results.push(result);
      if (!result.success) {
        showToast(result.message || "A pricing call failed", "error");
        return;
      }
    }
    const failed = results.filter((r) => !r.success);
    if (failed.length > 0) {
      showToast(failed[0].message || `${failed.length} pricing call(s) failed`, "error");
      return;
    }
    showToast(`Pricing saved (${results.length} record${results.length !== 1 ? "s" : ""})`, "success");
    advanceStep(7);
  };

  const handleStep1Continue = async () => {
    if (bookingId) {
      advanceStep(2);
      return;
    }

    let customer = resolveCustomer();

    if (!customer && data.isNewCustomer) {
      const nc = data.newCustomer;

      try {
        if (data.customerType === "Company") {
          const contactEmailMissing = nc.hasContactEmail !== false && !nc.contactEmail;
          if (!nc.companyName || !nc.legalName || !nc.vatNumber || !nc.industry || !nc.country || !nc.invoiceEmail || !nc.contactFirstName || !nc.contactLastName || contactEmailMissing || !nc.contactPhone) {
            showToast("Please fill in all required company fields", "error");
            return;
          }
          const created = await createProfessionalCustomer({
            companyName: nc.companyName.trim(),
            legalName: nc.legalName.trim(),
            vatNumber: nc.vatNumber.trim(),
            industry: nc.industry,
            website: nc.website.trim() || undefined,
            address: {
              street: nc.street.trim() || undefined,
              city: nc.city.trim() || undefined,
              zip: nc.zip.trim() || undefined,
              country: nc.country || undefined,
            },
            invoicing: {
              email: nc.invoiceEmail.trim().toLowerCase(),
              address: nc.invoiceAddress.trim() || undefined,
              paymentTerms: nc.paymentTerms,
            },
            primaryContact: {
              name: `${nc.contactFirstName.trim()} ${nc.contactLastName.trim()}`.trim(),
              email: nc.hasContactEmail !== false ? nc.contactEmail.trim().toLowerCase() : "",
              phone: nc.contactPhone.trim(),
              role: nc.contactRole || undefined,
              language: nc.contactLanguage || undefined,
            },
          });
          const createdData = created?.data ?? created;
          const newCust = {
            id: createdData.id,
            type: "Company",
            name: createdData.tradingName || nc.companyName,
            email: createdData.invoiceEmail || nc.invoiceEmail,
            phone: createdData.primaryContactPhone || nc.contactPhone,
            country: createdData.country || nc.country,
            customerCode: createdData.customerCode,
          };
          setData((prev) => ({ ...prev, customer: newCust, isNewCustomer: false }));
          customer = {
            customerId: newCust.id,
            customerType: "Company",
            customerName: newCust.name,
            customerEmail: newCust.email,
            customerPhone: newCust.phone,
          };
        } else {
          const emailMissing = nc.hasEmail !== false && !nc.email;
          if (!nc.firstName || !nc.lastName || emailMissing || !nc.phone || !nc.nationality || !nc.language) {
            showToast("Please fill in all required fields", "error");
            return;
          }
          const created = await createPrivateCustomer({
            firstName: nc.firstName.trim(),
            lastName: nc.lastName.trim(),
            hasEmail: nc.hasEmail !== false,
            email: nc.hasEmail !== false ? nc.email.trim().toLowerCase() : "",
            phone: nc.phone.trim(),
            dateOfBirth: nc.dateOfBirth || undefined,
            gender: nc.gender || undefined,
            nationality: nc.nationality,
            language: nc.language,
            address: {
              street: nc.street.trim() || undefined,
              houseNumber: nc.houseNumber.trim() || undefined,
              boxNumber: nc.boxNumber.trim() || undefined,
              city: nc.city.trim() || undefined,
              zip: nc.zip.trim() || undefined,
              country: nc.country || undefined,
            },
          });
          const createdData = created?.data ?? created;
          const newCust = {
            id: createdData.id,
            type: "Private",
            name: `${createdData.firstName || nc.firstName} ${createdData.lastName || nc.lastName}`.trim(),
            email: createdData.email || nc.email,
            phone: createdData.phone || nc.phone,
            country: createdData.country || nc.country,
            customerCode: createdData.customerCode,
          };
          setData((prev) => ({ ...prev, customer: newCust, isNewCustomer: false }));
          customer = {
            customerId: newCust.id,
            customerType: "Private",
            customerName: newCust.name,
            customerEmail: newCust.email,
            customerPhone: newCust.phone,
          };
        }
        showToast("Customer created successfully", "success");
      } catch (err) {
        showToast(err?.message || "Failed to create customer", "error");
        return;
      }
    }

    if (!customer) {
      showToast("Please select or create a customer to continue", "error");
      return;
    }

    const result = await createBooking({
      ...customer,
      bookingType: data.bookingType,
      internalNotes: (data.notes || []).map((n) => n.text).filter(Boolean).join("\n") || undefined,
    });
    if (result.success) {
      setBookingId(result.data.id);
      setBookingRef(result.data.bookingReference);
      showToast(`Booking ${result.data.bookingReference} created`, "success");
      advanceStep(2);
    } else {
      showToast(result.message || "Failed to create booking", "error");
    }
  };

  const goNext = () => {
    if (step === 1) { handleStep1Continue(); return; }
    if (step === 2) { handleStep2Continue(); return; }
    if (step === 3) { handleStep3Continue(); return; }
    if (step === 4) { handleStep4Continue(); return; }
    if (step === 5) { handleStep5Continue(); return; }
    if (step === 6) { handleStep6Continue(); return; }
    advanceStep(Math.min(7, step + 1));
  };
  const goBack = () => advanceStep(Math.max(1, step - 1));
  const goToStep = (s) => advanceStep(s);
  // Step 7 final button: submit booking (Draft → Pending).
  // A manager/admin will separately confirm it from the booking detail page.
  const handleSubmit = async () => {
    if (!bookingId) {
      showToast("Booking has not been created yet", "error");
      return;
    }
    const result = await submitBookingApi(bookingId);
    if (!result.success) {
      showToast(result.message || "Failed to submit booking", "error");
      return;
    }
    const newStatus = result.data?.status || "Pending";
    showToast(`Booking ${bookingRef} submitted — status: ${newStatus}`, "success");

    // Close out the queue row this booking came from, so the same Airtuerk file
    // cannot be worked twice. Deliberately after the booking exists and never
    // fatal: a failed link leaves a real booking and a still-open import, which
    // is recoverable, where the reverse would not be.
    if (prefill?.importId) {
      const linked = await linkImport(prefill.importId, bookingId);
      if (linked.success) showToast(`Import ${prefill.importSummary?.locator ?? ""} marked as booked`.trim(), "success");
      else showToast(linked.message || "Booking saved, but the import could not be marked as booked", "error");
    }

    navigate("/bookings");
  };
  const fromCustomer = prefill?.customer;

  const savingPricing = savingFlightPricing || savingHotelPricing || savingTransferPricing || savingGenericPricing || savingSSRPricing;


  const creatingCustomer = creatingPrivateCustomer || creatingProfessionalCustomer;

  const continueLabel = (() => {
    if ((creatingBooking || creatingCustomer) && step === 1) return "Creating...";
    if (savingProducts && step === 2) return "Saving...";
    if (savingTravelers && step === 3) return "Saving...";
    if (savingAssignments && step === 4) return "Saving...";
    if (savingSSRs && step === 5) return "Saving...";
    if (savingPricing && step === 6) return "Saving...";
    return null;
  })();

  const isContinueDisabled = !canProceed || creatingBooking || creatingCustomer || savingProducts || savingTravelers || savingAssignments || savingSSRs || savingPricing;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.headerBack} onClick={() => navigate(-1)}>{Icons.arrowLeft}</button>
          <div>
            <h1 className={styles.headerTitle}>New Booking{fromCustomer && <span className={styles.headerCustomerHint}> for {fromCustomer.name}</span>}</h1>
            <div className={styles.headerMeta}>
              <span className={styles.headerRef}>{bookingRef || "Reference pending"}</span>
              <span className={styles.headerDot}>·</span>
              <span className={styles.headerStatus}>Draft</span>
              {data.isLocked && <><span className={styles.headerDot}>·</span><span className={styles.headerLocked}>{Icons.lock} Locked</span></>}
            </div>
          </div>
        </div>
        <div className={styles.headerCenter}>
          <div className={styles.headerDateBadge}>{Icons.calendar}<span>{currentDate}</span></div>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnGhost} onClick={() => console.log("Draft saved:", data)}>{Icons.save} Save Draft</button>
          <button className={styles.btnDanger} onClick={() => navigate(-1)}>{Icons.trash} Discard</button>
        </div>
      </header>
      <HorizontalStepper currentStep={step} onStepClick={goToStep} />
      <div className={`${styles.layout} ${step >= 6 ? styles.layoutFull : ""}`}>
        <main className={styles.main}>
          {step === 7 ? (
            <StepSummary
              data={data}
              bookingRef={bookingRef}
              onGoToStep={goToStep}
              onSubmit={handleSubmit}
              onBack={goBack}
              submitting={submittingBooking}
            />
          ) : (
            <>
              <div className={styles.stepHeader}>
                <h2 className={styles.stepHeaderTitle}>{STEPS[step - 1]?.label}</h2>
                <span className={styles.stepHeaderCount}>Step {step} of {STEPS.length}</span>
              </div>
              <div className={styles.stepBody}>
                {step === 1 && <StepBookingDetails data={data} onChange={handleChange} />}
                {step === 2 && <StepProducts data={data} onChange={handleChange} onRemoveProduct={handleRemoveProduct} />}
                {step === 3 && <StepTravelers data={data} onChange={handleChange} onRemoveTraveler={handleRemoveTraveler} />}
                {step === 4 && <StepAssignments data={data} onChange={handleChange} onRemoveAssignment={handleRemoveAssignment} />}
                {step === 5 && <StepSSR data={data} onChange={handleChange} onRemoveSSR={handleRemoveSSR} onPersistSSRStatus={handlePersistSSRStatus} />}
                {step === 6 && <StepPricing data={data} onChange={handleChange} onCalculateVAT={calculateVAT} />}
              </div>
              <div className={styles.stepFooter}>
                {step > 1 && <button className={styles.btnSecondary} onClick={goBack}>{Icons.arrowLeft} Previous</button>}
                <div className={styles.footerSpacer} />
                <button
                  className={`${styles.btnPrimary} ${isContinueDisabled ? styles.btnDisabled : ""}`}
                  onClick={goNext}
                  disabled={isContinueDisabled}
                >
                  {continueLabel || <>Continue {Icons.arrowRight}</>}
                </button>
              </div>
            </>
          )}
        </main>
        {step < 6 && <LivePreview data={data} bookingRef={bookingRef} />}
      </div>
    </div>
  );
};

export default AddBooking;
